import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import API from '../../API/Main/API';
import {
	ClientScheduleOverviewViewModel,
	CreateRecurringSchedulePayload,
	GetOverviewParams,
	RecurringScheduleViewModel,
	UpdateRecurringSchedulePayload,
} from '../../API/ClientAPIHelpers/recurringSchedule';
import { AiParseScheduleRequest, AiParseScheduleResponse } from '../../API/ClientAPIHelpers/ai';
import { ErrorProperties } from '../../API/identityAPIProperties';

interface RecurringScheduleState {
	error: ErrorProperties;
	isCreating: boolean;
	loading: boolean;
	overview: ClientScheduleOverviewViewModel[];
}

const initialState: RecurringScheduleState = {
	overview: [],
	loading: false,
	isCreating: false,
	error: { status: 0, title: '' },
};

export const getScheduleOverview = createAsyncThunk(
	'recurringSchedule/getOverview',
	async (params: GetOverviewParams | undefined, thunkAPI) => {
		const response = await API.ClientAPI.RecurringSchedule.getOverview(params);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as ClientScheduleOverviewViewModel[];
	},
);

export const createRecurringSchedules = createAsyncThunk(
	'recurringSchedule/create',
	async (data: CreateRecurringSchedulePayload[], thunkAPI) => {
		const response = await API.ClientAPI.RecurringSchedule.create(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as RecurringScheduleViewModel[];
	},
);

export const parseScheduleWithAi = createAsyncThunk(
	'recurringSchedule/parseScheduleWithAi',
	async (data: AiParseScheduleRequest, thunkAPI) => {
		const response = await API.ClientAPI.Ai.parseSchedule(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		if (!response.schedules?.length) {
			return thunkAPI.rejectWithValue({
				status: 0,
				title: 'AI returned an unexpected response format. Please rephrase your prompt.',
			});
		}

		return response as AiParseScheduleResponse;
	},
);

export const deleteRecurringSchedule = createAsyncThunk(
	'recurringSchedule/delete',
	async (id: string, thunkAPI) => {
		const response = await API.ClientAPI.RecurringSchedule.delete(id);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return id;
	},
);

export const updateSchedule = createAsyncThunk(
	'recurringSchedule/update',
	async ({ id, data }: { data: UpdateRecurringSchedulePayload; id: string }, thunkAPI) => {
		const response = await API.ClientAPI.RecurringSchedule.update(id, data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as RecurringScheduleViewModel;
	},
);

export const updateScheduleEndDate = createAsyncThunk(
	'recurringSchedule/updateEndDate',
	async ({ id, endDate }: { endDate: string | null; id: string }, thunkAPI) => {
		const response = await API.ClientAPI.RecurringSchedule.updateEndDate(id, endDate);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

const recurringScheduleSlice = createSlice({
	name: 'recurringSchedule',
	initialState,
	reducers: {
		clearOverview: (state) => {
			state.overview = [];
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getScheduleOverview.pending, (state) => {
				state.loading = true;
			})
			.addCase(getScheduleOverview.fulfilled, (state, action) => {
				state.loading = false;
				state.overview = action.payload;
			})
			.addCase(getScheduleOverview.rejected, (state, action) => {
				state.loading = false;
				state.error = action.error as ErrorProperties;
			})
			.addCase(createRecurringSchedules.pending, (state) => {
				state.isCreating = true;
			})
			.addCase(createRecurringSchedules.fulfilled, (state) => {
				state.isCreating = false;
			})
			.addCase(createRecurringSchedules.rejected, (state, action) => {
				state.isCreating = false;
				state.error = action.error as ErrorProperties;
			})
			.addCase(deleteRecurringSchedule.fulfilled, (state, action) => {
				state.overview = state.overview
					.map((client) => {
						const schedules = client.schedules.filter((s) => s.id !== action.payload);

						return { ...client, schedules, activeScheduleCount: schedules.length };
					})
					.filter((client) => client.schedules.length > 0);
			});
	},
});

export const { clearOverview } = recurringScheduleSlice.actions;
export default recurringScheduleSlice.reducer;
